/**
 * Runtime API base URL.
 *
 * Resolved at module load with this precedence (uses `??`, so an explicit empty
 * string from the runtime layer is honoured as "same-origin"):
 *   1. `window._env_.REACT_APP_SERVER_URL`  - injected at runtime by the backend
 *      `/env-config.js` endpoint (or by `public/env-config.js` during local dev).
 *      In production same-origin deployment this is an empty string, which means
 *      "use a relative URL for API calls" — DO NOT treat it as missing.
 *   2. `import.meta.env.REACT_APP_SERVER_URL` - baked in at build time from `.env*`.
 *      In production builds `.env.production` forces this to empty (defence in depth).
 */
declare global {
  interface Window {
    _env_?: {
      REACT_APP_SERVER_URL?: string;
    };
  }
}

const SERVER_ORIGIN: string =
  window._env_?.REACT_APP_SERVER_URL ?? import.meta.env.REACT_APP_SERVER_URL ?? '';

export const baseURL = `${SERVER_ORIGIN}/api`;

/**
 * Prepend the API base URL to a relative path. Absolute URLs are returned as-is so this
 * helper can be used safely with backend-provided URLs that may already be fully qualified
 * (e.g. CDN avatar URLs).
 *
 * @example
 *   composeURL('/media/v1/avatar/abc.png') // 'https://stg-core-api.cgm.org.tw/api/media/v1/avatar/abc.png'
 *   composeURL('https://cdn.example.com/x') // 'https://cdn.example.com/x'
 */
export const composeURL = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  return `${baseURL}${path}`;
};
